import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const FrameworkDetails = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      setScrollProgress(latest);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  // Title reveal
  const titleOpacity = useTransform(scrollYProgress, [0.15, 0.25], [0, 1]);
  const titleY = useTransform(scrollYProgress, [0.15, 0.25], [40, 0]);

  // Step cards progressive reveal
  const step1Progress = Math.max(0, Math.min(1, (scrollProgress - 0.3) * 4));
  const step2Progress = Math.max(0, Math.min(1, (scrollProgress - 0.35) * 4));
  const step3Progress = Math.max(0, Math.min(1, (scrollProgress - 0.4) * 4));

  // CTA reveal
  const ctaOpacity = useTransform(scrollYProgress, [0.5, 0.6], [0, 1]);
  const ctaY = useTransform(scrollYProgress, [0.5, 0.6], [30, 0]);

  // Background glow
  const glowOpacity = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0, 0.15, 0.05]);

  return (
    <section ref={containerRef} className="relative py-32 md:py-40 lg:py-48 px-6 md:px-8">
      {/* Section Background Glow */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: glowOpacity }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-accent-blue rounded-full blur-[140px]"></div>
      </motion.div>

      <div className="relative z-10 max-w-[1200px] mx-auto">
        <motion.div
          style={{
            opacity: titleOpacity,
            y: titleY
          }}
          className="text-center mb-16 md:mb-20 lg:mb-24"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 md:mb-8">
            How Frameworks Work
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-400 max-w-[700px] mx-auto leading-relaxed">
            Each framework follows a systematic approach to identify constraints and multiply output.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          {/* Step 1 */}
          <motion.div
            style={{
              opacity: step1Progress,
              y: (1 - step1Progress) * 50,
              scale: 0.95 + step1Progress * 0.05
            }}
            className="relative bg-dark-light/40 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 md:p-10 hover:border-primary/50 transition-all duration-500"
          >
            <div className="flex items-start gap-4 md:gap-6 mb-6">
              <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                <span className="text-xl md:text-2xl font-bold text-primary">1</span>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Identify
                </h3>
                <p className="text-sm md:text-base text-gray-400 leading-relaxed">
                  Map your system to find the structural constraint limiting output.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Step 2 */}
          <motion.div
            style={{
              opacity: step2Progress,
              y: (1 - step2Progress) * 50,
              scale: 0.95 + step2Progress * 0.05
            }}
            className="relative bg-dark-light/40 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 md:p-10 hover:border-primary/50 transition-all duration-500"
          >
            <div className="flex items-start gap-4 md:gap-6 mb-6">
              <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                <span className="text-xl md:text-2xl font-bold text-primary">2</span>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Redesign
                </h3>
                <p className="text-sm md:text-base text-gray-400 leading-relaxed">
                  Apply the framework to remove the constraint and redesign the system.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Step 3 */}
          <motion.div
            style={{
              opacity: step3Progress,
              y: (1 - step3Progress) * 50,
              scale: 0.95 + step3Progress * 0.05
            }}
            className="relative bg-dark-light/40 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 md:p-10 hover:border-primary/50 transition-all duration-500"
          >
            <div className="flex items-start gap-4 md:gap-6 mb-6">
              <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                <span className="text-xl md:text-2xl font-bold text-primary">3</span>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Multiply
                </h3>
                <p className="text-sm md:text-base text-gray-400 leading-relaxed">
                  Watch output increase without adding headcount or capital.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          style={{
            opacity: ctaOpacity,
            y: ctaY
          }}
          className="text-center mt-20 md:mt-24 lg:mt-28"
        >
          <a
            href="/apply"
            className="inline-flex items-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,102,255,0.4)] text-base md:text-lg whitespace-nowrap min-h-[56px] cursor-pointer"
          >
            Apply for a Force Multiplier Audit
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FrameworkDetails;